import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  InputAdornment,
  IconButton,
  Switch,
  Skeleton,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon, 
  Visibility, 
  VisibilityOff
} from '@mui/icons-material';

import userService from '../../services/user.service';

// Phone number validation regex
const phoneRegExp = /^\+?[0-9]{10,15}$/;

// Validation schema for user profile
const UserProfileSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  msisdn: Yup.string().matches(phoneRegExp, 'Phone number is not valid').nullable(),
  role: Yup.string().required('Role is required'),
  is_active: Yup.boolean()
});

// Validation schema for password change
const PasswordSchema = Yup.object().shape({
  newPassword: Yup.string().min(8, 'Password must be at least 8 characters').required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Confirm password is required')
});

function UserEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await userService.getUserById(id);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to fetch user:', err);
      setError('Failed to load user data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (values) => {
    setSaveLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await userService.updateUser(id, values);
      setSuccess(true);
      // Update local user data with the updated values
      setUser(values);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handlePasswordSubmit = async (values) => {
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      await userService.changeUserPassword(id, values);
      setPasswordSuccess(true);
    } catch (err) {
      console.error('Failed to change password:', err);
      setPasswordError(err.message || 'Failed to change password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
          sx={{ mb: 2 }}
        >
          Back to Users
        </Button>
        <Typography variant="h4" component="h1" gutterBottom>
          {loading ? <Skeleton width={300} /> : `Edit User: ${user?.name || 'User'}`}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {loading ? <Skeleton width={400} /> : 'Update user information and manage access'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          User updated successfully!
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Profile Information" />
          <Tab label="Change Password" />
        </Tabs>
      </Box>

      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <CardContent>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="text" height={60} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
              <Skeleton variant="text" height={60} sx={{ mb: 2, mt: 4 }} />
              <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={30} width={200} sx={{ mb: 2 }} />
            </Box>
          ) : user ? (
            <>
              {/* Profile Information Tab */}
              {tabValue === 0 && (
                <Formik
                  initialValues={{
                    name: user.name || '',
                    email: user.email || '',
                    msisdn: user.msisdn || '',
                    role: user.role || 'user',
                    is_active: user.is_active !== undefined ? user.is_active : true
                  }}
                  validationSchema={UserProfileSchema}
                  onSubmit={handleProfileSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, values, setFieldValue }) => (
                    <Form>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom>
                            Basic Information
                          </Typography>
                          <Divider sx={{ mb: 3 }} />

                          <Field
                            as={TextField}
                            name="name"
                            label="Full Name"
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                          />

                          <Field
                            as={TextField}
                            name="email"
                            label="Email Address"
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                          />

                          <Field
                            as={TextField}
                            name="msisdn"
                            label="Phone Number"
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            placeholder="+233..."
                            error={touched.msisdn && Boolean(errors.msisdn)}
                            helperText={touched.msisdn && errors.msisdn}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom>
                            Access Control
                          </Typography>
                          <Divider sx={{ mb: 3 }} />

                          <Box sx={{ mt: 2 }}>
                            <FormControl component="fieldset" sx={{ mb: 2 }}>
                              <FormLabel component="legend">User Role</FormLabel>
                              <RadioGroup
                                row
                                name="role"
                                value={values.role}
                                onChange={(e) => setFieldValue('role', e.target.value)}
                              >
                                <FormControlLabel value="admin" control={<Radio />} label="Admin" />
                                <FormControlLabel value="user" control={<Radio />} label="Regular User" />
                              </RadioGroup>
                            </FormControl>
                          </Box>

                          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <Switch
                              checked={values.is_active}
                              onChange={(e) => setFieldValue('is_active', e.target.checked)}
                              color="success"
                            />
                            <Typography>
                              {values.is_active ? 'Active Account' : 'Inactive Account'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/users')}
                          sx={{ mr: 2 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          startIcon={saveLoading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
                          disabled={saveLoading}
                        >
                          {saveLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </Box>
                    </Form>
                  )}
                </Formik>
              )}

              {/* Change Password Tab */}
              {tabValue === 1 && (
                <Box sx={{ mt: 2 }}>
                  {passwordSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      Password updated successfully!
                    </Alert>
                  )}

                  {passwordError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {passwordError}
                    </Alert>
                  )}

                  <Formik
                    initialValues={{
                      newPassword: '',
                      confirmPassword: ''
                    }}
                    validationSchema={PasswordSchema}
                    onSubmit={handlePasswordSubmit}
                  >
                    {({ errors, touched, resetForm }) => (
                      <Form>
                        <Field
                          as={TextField}
                          name="newPassword"
                          label="New Password"
                          type={showNewPassword ? 'text' : 'password'}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.newPassword && Boolean(errors.newPassword)}
                          helperText={touched.newPassword && errors.newPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  edge="end"
                                >
                                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />

                        <Field
                          as={TextField}
                          name="confirmPassword"
                          label="Confirm New Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="button"
                            variant="outlined"
                            sx={{ mr: 2 }}
                            onClick={() => {
                              resetForm();
                              setPasswordError(null);
                              setPasswordSuccess(false);
                            }}
                          >
                            Reset
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={passwordLoading}
                            startIcon={passwordLoading ? <CircularProgress size={24} color="inherit" /> : null}
                          >
                            {passwordLoading ? 'Updating...' : 'Update Password'}
                          </Button>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="error">
              User not found. The user may have been deleted or you may not have permission to view it.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default UserEdit;